<template>
  <WidgetCard title="Inkomna beställningar" subtitle="Att granska, godkänna eller bekräfta">
    <v-data-table
      hide-default-footer
      :headers="_headers"
      :items="uniqBy(orders, 'orderId')"
      :server-items-length="totalRows"
      :items-per-page="itemsPerPage"
      item-key="orderId"
      class="mt-n2 clickable"
      @click:row="clickOrder"
    >
      <template slot="item.id" slot-scope="props" v-if="companyId && get(props, 'item.orderId', null)">
        <div style="width: 115px;">{{ props.item.orderId }}</div>
      </template>
      <!-- Person id header -->
      <template
        slot="item.personId"
        slot-scope="props"
      >
      <div style="width: 140px;">
        {{ get(props, 'item.firstName', '') + " " + get(props, 'item.lastName', '') }} ({{ get(props, 'item.personalNumber', '') | formatSecretPnr }})
      </div>
      </template>

      <!-- Company id header -->
      <template slot="item.companyId" slot-scope="props" v-if="props.item&&props.item.companyName">
        <div style="width: 115px;">{{ props.item.companyName }}</div>
      </template>
      <!-- Contract type header -->
      <template slot="item.contractType" slot-scope="props" v-if="get(props, 'item.company.contracttype.name', null)">
        <div style="width: 170px;">{{ get(props, 'item.company.contracttype.name', '') }}</div>
      </template>
      <!-- Region id header -->
      <template slot="item.regionId" slot-scope="props" v-if="props.item&&props.item.regionName">
        <div style="width: 170px;">{{ props.item.regionName }}</div>
      </template>
      <!-- Contact Person id header -->
      <template slot="item.contactPersonId" slot-scope="props">
        <div style="width: 170px;" v-if="props.item&&props.item.contactPersonFirstName" >
          {{ get(props, 'item.contactPersonFirstName', '') + " " + get(props, 'item.contactPersonLastName', '') }}
        </div>
      </template>
      <!-- Order createdat header -->
      <template slot="item.createdAt" slot-scope="props" v-if="props.item && props.item.createdAt">
        <div style="width: 100px;">{{ get(props, 'item.createdAt', '') | date }}</div>
      </template>
      <!-- Order stateId header -->
      <template slot="item.stateId" slot-scope="props">
        <div style="width: 218px" :class="`${setStateClass(props.item)}`" v-if="props.item&&props.item.state&&props.item.stateName">
          <v-icon>{{ displayOrderIcon(get(props, 'item.type')) }}</v-icon>
          {{ get(props, 'item.stateName', '') }}
        </div>
      </template>

      <template slot="item.action" slot-scope="props" v-if="get(props, 'item.pauseType', null) === 'basicEducationPause'">
        <v-icon @click.stop.prevent="changeStatus(props.item)" class="clickable d-flex align-center">$sl-edit</v-icon>
      </template>
      
      <!-- Book pause type -->
      <template slot="item.type" slot-scope="props" v-if="get(props, 'item.pauseType', null) || get(props, 'item.type', null) === 'ExtensionOrder'">
        <div style="width: 120px;" class="d-flex align-center">{{ props.item.shouldPauseTheoryCourse ? 'Uppehåll' : 'Förlängning' }}</div>
      </template>

      <!-- Order cost -->
      <template slot="item.price" slot-scope="props" v-if="companyId && get(props, 'item.price', null)">
        {{ get(props, 'item.price', '') | currency | withCurrencyCode }}
      </template>
    </v-data-table>
  </WidgetCard>
</template>


<script>
import API from '@/services/api'
import get from 'lodash/get'
import WidgetCard from '@/components/widgets/WidgetCard.vue'
import { mapGetters } from 'vuex'
import uniqBy from 'lodash/uniqBy'

// @group Widgets
// ApplicationApprentice component
export default {

  name: 'Applications',

  components: {
    WidgetCard
  },

  props: {
    companyId: {
      type: Number,
      default: null
    }
  },

  data: () => ({
    totalRows: 0,
    itemsPerPage: 500,
    orders: [],

    advSearch: {
      stateId: [2,5]
    },
    headers: [
      {text: 'Avser', value: 'personId', width: '160'},
      {text: 'Avtalstillhörighet', value: 'contracttype', width: '210'},
      {text: 'Företag', value: 'companyId', width: '120'},
      {text: 'Kontaktperson', value: 'contactPersonId', width: '180'},
      {text: 'Region', value: 'regionId', width: '180'},
      {text: 'Status', value: 'stateId', width: '180'},
      {text: 'Datum', value: 'createdAt', width: '100'},
      {text: '', value: 'notes', sortable: false, width: '50'}
    ]
  }),

  computed: {
    ...mapGetters('currentuser', ['currentUser', 'handledCasesAreVisible']),

    _headers() {
      // Change headers if for company overview page
      if ( this.companyId ) {
        const filteredHeaders = this.headers.filter(h => h.text !== 'Datum')
        const newHeader = { text: 'Belopp (exkl. moms)', value: 'price', width: '200' }
        const modifiedHeaders = [ ...filteredHeaders.slice(0, 4), newHeader, ...filteredHeaders.slice(4, filteredHeaders.length) ]
        modifiedHeaders.unshift({text: 'Nr', value: 'id', width: '160'})
        return modifiedHeaders
      }

      return this.headers
    }
  },

  watch: {
    handledCasesAreVisible: {
      handler(val) {
        this.fetchList()
      }
    }
  },

  async created() {
    await this.init()
  },

  methods: {
    get,

    uniqBy,

    setStateClass(item = {}) {
      
      let className = `state state-${ item.state }`

      if ( get(item, 'shouldPauseTheoryCourse', false) ) {
        className = ''
      } 

      if ( get(item, 'type', null) === 'PrintOrder' ) {
        className = `state print-state-${ item.state }`
      }

      return className
    },

    async init() {
      await this.fetchList()
      await this.fetchBookPauses()

      if(this.orders?.some(o => o.shouldPauseTheoryCourse && !o.markedAsRead)) {
        this.headers.push(
          {text: 'Typ', value: 'type', width: '80'},
          {text: 'Ändra', value: 'action', width: '80'}
        )
      }
    },

    async changeStatus(item) {
      const res = await this.$dialog.confirm({
        text: "När du markerar ärendet som läst så försvinner ärendet från översikten.",
        title: "Är distansleverantören informerad om uppehållet?",
        width: "500",
        actions: [
          { text: "Avbryt", key: false, icon: "$sl-cancel", outlined: true },
          { text: "Markera som läst", key: true, icon: "$sl-like" },
        ],
      });

      if (res !== true) return;

      API.updatePauseBook(get(item, 'id', -1), { data: { markedAsRead: true }}).then(() => {
        return this.init()
      })
    },

    clickOrder(item) {

      //- Declare `key:value` pairs for order type and route
      const types = {
        'ExtensionOrders': 'ExtensionOrder', 
        'PrintOrder': 'PrintOrderDetail'
      }

      let navigateTo = { name: 'OrderDetail', params: { id: item.id }}

      Object.entries(types).map(([key, value]) => {
        if ( key === item.type ) 
          navigateTo = {name: value, params: {id: item.id} }
      })

      //- Other condition
      if(item?.shouldPauseTheoryCourse) 
        navigateTo = { name: 'PersonDetail', params: { id: item.personId }, query: { tab: '4' } }

      this.$nextTick(() => this.$router.push(navigateTo))

    },

    async fetchList() {
      this.advSearch = {
        stateId: [2, 5]
      }

      //- Filter orders by `companyId`
      if ( this.companyId ) {
        this.advSearch = {
          stateId: [1,2],
          companyId: this.companyId,
          view: 'company-overview'
        }
      }
      
      this.criteria = {
        startAt: 0,
        pageSize: this.itemsPerPage,
        sortDesc: true,
        advSearch: { view: 'overview', ...this.advSearch }
      }

      if ( this.handledCasesAreVisible ) {
        this.advSearch = { createdById: this.currentUser.id, stateId: [2] }

        this.criteria = {
          ...this.criteria,
          pageSize: 500,
          advSearch: this.advSearch
        }
      }

      try {
        const response = await API.listOrders(this.criteria)
        const filter = it => it.stateName !== 'Beställd' && [...this.advSearch.stateId].includes(Number(it.stateId))

        if ( !this.handledCasesAreVisible )
          response.data.rows = response.data.rows.filter(filter);

        this.orders = response.data.rows
        this.totalRows = this.orders?.length

      } catch(error) {
        console.log('There was an error:', error)
      }
    },
    async fetchBookPauses() {
      try {
        const response = await API.listBookPauses()

        const filter = o => (o.shouldPauseTheoryCourse && !o.markedAsRead && o.status === 'approved')
        const bookPauses = response.data.rows.filter(filter).map(bookPause => {
          return {
            ...bookPause,
            personId: bookPause.book?.personId,
            firstName: bookPause.book?.person?.firstName,
            lastName: bookPause.book?.person?.lastName,
            personalNumber: bookPause.book?.person?.personalNumber,
            companyId: bookPause.book?.apprenticeship?.companyId,
            companyName: bookPause.book?.apprenticeship?.company?.name,
            contactPersonId: bookPause.approvedBy?.id,
            contactPersonFirstName: bookPause.approvedBy?.firstName,
            contactPersonLastName: bookPause.approvedBy?.lastName,
            regionId: bookPause.regionId,
            regionName: bookPause.region?.name,
            state: bookPause.status,
            stateName: bookPause.status === 'approved' ? 'Godkänd' : '',
            sentToCompanyForApprovalAt: bookPause?.approvedAt
          }
        })

        this.orders = this.orders.concat(bookPauses)
        
        this.totalRows = this.orders.length;

      } catch (error) {
        console.log('There was an error:', error)
      }
    },

    displayOrderIcon(orderType) {
      if ( orderType === 'PrintOrder') return '$sl-history-education'
      if ( orderType === 'DistanceOrder' ) return '$sl-delivery-truck-bold'
      return ''
    }
  }
}
</script>

<style scoped>
</style>
